// Pure tree-selection logic for the destination picker.
//
// `flatDestinations` is the browse tree returned by the API:
//   { countries: [{ code, name, regions: [{ code, name, resorts: [...] }] }] }
// `selection` is the user's current picks as an array of { code, name }.
//
// Selecting a parent implicitly selects all its descendants (codes are
// prefix-encoded), so the UI shows children as selected and we "roll up" a
// fully-selected parent into a single entry.

import { isHotelCode } from "./geography.js";

// A code is shown as selected when it - or any of its ancestors - is selected.
export const isVisuallySelected = (selection, code) =>
    selection.some((d) => d.code && code.startsWith(d.code));

// Every node whose code is `length` chars and sits under `ancestorCode` -
// i.e. that ancestor's descendants at a given level.
export const collectAtLevel = (flatDestinations, ancestorCode, length) => {
    const out = [];
    for (const country of (flatDestinations?.countries || [])) {
        if (country.code.length == length && country.code.startsWith(ancestorCode)) out.push(country);
        for (const region of (country.regions || [])) {
            if (region.code.length == length && region.code.startsWith(ancestorCode)) out.push(region);
            for (const resort of (region.resorts || [])) {
                if (resort.code.length == length && resort.code.startsWith(ancestorCode)) out.push(resort);
            }
        }
    }
    return out;
};

// Roll the selection up: if every child of a node is selected, replace them
// with the node itself (recursively, so all regions -> the country). Codes not
// in the geography tree (e.g. hotels from search) pass through, in order.
export const normalizeSelection = (flatDestinations, selection) => {
    if (!flatDestinations?.countries) return selection;
    const selectedCodes = new Set(selection.map((d) => d.code));
    const childrenOf = (node) => node.regions || node.resorts || [];
    const isFully = (node) => {
        if (selectedCodes.has(node.code)) return true;
        const kids = childrenOf(node);
        return kids.length > 0 && kids.every(isFully);
    };
    const result = [];
    const seen = new Set();
    const push = (code, name) => { if (!seen.has(code)) { seen.add(code); result.push({ code, name }); } };
    const walk = (node) => {
        if (isFully(node)) { push(node.code, node.name); return; }
        childrenOf(node).forEach(walk);
    };
    flatDestinations.countries.forEach(walk);
    // Keep selections that aren't part of the geography tree (hotels).
    const treeCodes = new Set();
    const collect = (node) => { treeCodes.add(node.code); childrenOf(node).forEach(collect); };
    flatDestinations.countries.forEach(collect);
    selection.forEach((d) => { if (!treeCodes.has(d.code)) push(d.code, d.name); });
    return result;
};

// Toggle a destination in/out, returning the new (normalized) selection.
//
// Hotels are mutually exclusive with geography (you book a specific hotel OR
// search areas), but several hotels can be chosen together. Geography uses the
// tree logic: deselecting a child that's only selected via its parent expands
// the parent into its other children.
export const toggleDestination = (flatDestinations, selection, selected) => {
    // Hotels: toggle within the hotel-only subset, dropping any geography.
    if (isHotelCode(selected.code)) {
        const hotels = selection.filter((d) => isHotelCode(d.code));
        if (hotels.some((d) => d.code == selected.code)) {
            return hotels.filter((d) => d.code != selected.code);
        }
        return [...hotels, { name: selected.name, code: selected.code }];
    }

    // Selecting geography drops any hotels, then applies the tree logic.
    const base = selection.filter((d) => !isHotelCode(d.code));

    // Exactly selected -> simple deselect.
    if (base.some((d) => d.code == selected.code)) {
        return normalizeSelection(flatDestinations, base.filter((d) => d.code != selected.code));
    }
    // Selected only via an ancestor (parent): "unchecking" this child replaces
    // that ancestor with all of its descendants at this level, minus the
    // clicked one - keeping everything else selected.
    const ancestor = base
        .filter((d) => d.code && selected.code != d.code && selected.code.startsWith(d.code))
        .sort((a, b) => b.code.length - a.code.length)[0];
    if (ancestor) {
        const siblings = collectAtLevel(flatDestinations, ancestor.code, selected.code.length)
            .filter((n) => n.code != selected.code)
            .map((n) => ({ name: n.name, code: n.code }));
        return normalizeSelection(flatDestinations, [...base.filter((d) => d.code != ancestor.code), ...siblings]);
    }
    // Otherwise it's a fresh pick.
    return normalizeSelection(flatDestinations, [...base, { name: selected.name, code: selected.code }]);
};
